import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioApi, errorKey } from '../../../data/studioApi';

// Fifteen a page: three rows of the desktop grid. A batch leaves out the profile's photos and
// carousels, so a short one is topped up from the next ones, a few requests at most.
const PAGE = 15;
const TOP_UP = 3;
const REQUOTE = ['usage_quote_consumed', 'usage_quote_mismatch', 'usage_quote_expired'];

// After a failed mapping the choice starts from it: the videos that worked in their places
// and, where one has to be swapped, a `failed` entry carrying why. A momentary failure keeps
// its video as it is.
const seed = retry => (retry ? retry.slots.map(slot => (slot.reason ? { ...slot.video, failed: slot.reason } : slot.video)) : []);
const blockedOf = retry => Object.fromEntries((retry?.slots || []).filter(slot => slot.reason).map(slot => [slot.video.id, slot.reason]));

// The picker's state (dialogs/PersonalityDialog): the profile's Reels a page at a time, the
// choice in its places (exactly `limits.personality_videos`), its quote, and the start. A
// pick fills the first place marked to swap, else the next empty one. A video the backend
// no longer accepts (`video_not_selectable`, `errors["video_ids.N"]`) or that a failed
// mapping could not use turns unselectable in place, with the reason.
export default function usePersonalityPicker(s) {
  const { myPersonality, profile } = s;
  const { retry } = myPersonality;
  const needed = s.capabilities?.limits?.personality_videos;
  const profileId = profile?.id;
  const [videos, setVideos] = useState([]);
  const [list, setList] = useState({ status: 'loading', cursor: null, more: false, error: null });
  const [selected, setSelected] = useState(() => seed(retry));
  const [blocked, setBlocked] = useState(() => blockedOf(retry));
  const [quote, setQuote] = useState({ status: 'idle', value: null, error: null });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  // Bumped to quote the same choice again (the quote expired, was used or no longer matches).
  const [attempt, setAttempt] = useState(0);
  const alive = useRef(true);
  const quoteRequest = useRef(0);
  const seeded = useRef(retry?.mappingId || null);
  useEffect(() => () => { alive.current = false; }, []);

  // A mapping that fails while the dialog is open starts the choice over from it.
  useEffect(() => {
    if (!retry || seeded.current === retry.mappingId) return;
    seeded.current = retry.mappingId;
    setSelected(seed(retry));
    setBlocked(previous => ({ ...previous, ...blockedOf(retry) }));
    setError(null);
  }, [retry?.mappingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPage = useCallback(async cursor => {
    setList(previous => ({ ...previous, status: cursor ? 'more' : 'loading', error: null }));
    try {
      let page = await studioApi.profileVideos(profileId, { limit: PAGE, ...(cursor ? { cursor } : {}) });
      const batch = [...page.data];
      for (let extra = 0; extra < TOP_UP && batch.length < PAGE && page.has_more && page.next_cursor; extra += 1) {
        page = await studioApi.profileVideos(profileId, { limit: PAGE, cursor: page.next_cursor });
        batch.push(...page.data);
      }
      if (!alive.current) return;
      setVideos(previous => {
        const known = new Set(cursor ? previous.map(item => item.id) : []);
        const fresh = batch.filter(item => !known.has(item.id) && known.add(item.id));
        return cursor ? [...previous, ...fresh] : fresh;
      });
      setList({ status: 'ready', cursor: page.next_cursor, more: page.has_more && !!page.next_cursor, error: null });
    } catch (failure) {
      if (alive.current) setList(previous => ({ ...previous, status: 'error', error: errorKey(failure) }));
    }
  }, [profileId]);
  useEffect(() => { if (profileId) loadPage(null); }, [profileId, loadPage]);

  const shown = useMemo(() => videos.map(item => (blocked[item.id] ? { ...item, selectable: false, unavailable_reason: blocked[item.id] } : item)), [videos, blocked]);

  // `errors["video_ids.N"]` → those videos are no longer selectable, with the reason given.
  const reject = (ids, errors = {}) => {
    const reasons = Object.fromEntries(Object.entries(errors).map(([field, messages]) => [ids[Number(field.split('.')[1])], messages?.[0]]).filter(([id]) => id));
    if (!Object.keys(reasons).length) return;
    setBlocked(previous => ({ ...previous, ...reasons }));
    setSelected(previous => previous.filter(item => !reasons[item.id]));
  };

  // Only a full choice with nothing left to swap is quoted; a later answer for an older one is dropped.
  const swaps = selected.filter(item => item.failed);
  const ids = selected.filter(item => !item.failed).map(item => item.id);
  const key = ids.join();
  useEffect(() => {
    const request = ++quoteRequest.current;
    if (ids.length !== needed || swaps.length) { setQuote({ status: 'idle', value: null, error: null }); return; }
    setQuote({ status: 'loading', value: null, error: null });
    myPersonality.quote(ids).then(value => {
      if (alive.current && quoteRequest.current === request) setQuote({ status: 'ready', value, error: null });
    }).catch(failure => {
      if (!alive.current || quoteRequest.current !== request) return;
      if (failure.code === 'video_not_selectable') reject(ids, failure.errors);
      setQuote({ status: 'error', value: null, error: errorKey(failure) });
    });
  }, [key, swaps.length, needed, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = video => {
    if (!video.selectable || starting) return;
    setError(null);
    setSelected(previous => {
      if (previous.some(item => item.id === video.id)) return previous.filter(item => item.id !== video.id);
      const swap = previous.findIndex(item => item.failed);
      if (swap >= 0) return previous.map((item, index) => (index === swap ? video : item));
      return previous.length >= needed ? previous : [...previous, video];
    });
  };
  const remove = id => { if (!starting) setSelected(previous => previous.filter(item => item.id !== id)); };
  const requote = () => setAttempt(count => count + 1);

  // Starts the mapping. Out of credits opens the plans (the PlanDialog takes the modal's place).
  const confirm = async () => {
    if (ids.length !== needed || swaps.length || quote.status !== 'ready' || starting) return false;
    setStarting(true);
    setError(null);
    try {
      return await myPersonality.start(ids, quote.value);
    } catch (failure) {
      const reason = errorKey(failure);
      if (reason === 'credits' && s.payments.outOfCredits()) return false;
      if (failure.code === 'video_not_selectable') reject(ids, failure.errors);
      else if (failure.code === 'personality_mapping_in_progress') myPersonality.refresh();
      else if (REQUOTE.includes(failure.code)) requote();
      if (alive.current) setError(reason);
      return false;
    } finally { if (alive.current) setStarting(false); }
  };

  return { needed, videos: shown, list, selected, swaps, quote, starting, error, toggle, remove, confirm, reload: () => loadPage(null), more: () => loadPage(list.cursor) };
}
